import { centsToPrice } from '@/utils/formatter';
import { Card, CardContent } from '@/components/ui/card';
import {
  useOrderFormContext,
  ContainerOrder,
} from '../PublicEventOrderForm/OrderFormContext';
import { useMemo, ReactNode } from 'react';
import { MerchantOrderItemUnit } from '@/graphql/generatedTypes';
import i18n from '@/utils/i18n';
import { OrderField } from '../PublicEventOrderForm/components/OrderFormFields';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FaQuestionCircle } from 'react-icons/fa';
import { smartFloor } from '@/utils/number';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import SvgIcon from '@/components/Icon/SvgIcon';

type CostSummaryProps = {
  subtotalCents: number;
  taxCents?: number;
  taxPercentage?: number;
  cleaningFeeCents?: number;
  // Percentage of the rented pieces that is always billed as cleaning;
  // null = no minimum, only items returned dirty are billed
  minimumCleaningFeeRate?: number | null;
  totalCents?: number;
  currency: string | null;
  children?: ReactNode;
  className?: string;
};

export function CostSummary({
  subtotalCents,
  taxCents: providedTaxCents,
  taxPercentage,
  cleaningFeeCents = 0,
  minimumCleaningFeeRate = null,
  totalCents: providedTotalCents,
  currency,
  children,
  className,
}: CostSummaryProps) {
  // Calculate tax and total if taxPercentage is provided
  // Tax is calculated on subtotal + cleaning fee
  const baseForTax = subtotalCents + cleaningFeeCents;
  const calculatedTaxCents = taxPercentage
    ? (baseForTax * taxPercentage) / 100
    : 0;
  const taxCents = providedTaxCents ?? calculatedTaxCents;
  const calculatedTotalCents = subtotalCents + cleaningFeeCents + taxCents;
  const totalCents = providedTotalCents ?? calculatedTotalCents;

  return (
    <Card className={cn('tw:sticky tw:top-0', className)}>
      <CardContent className="tw:flex tw:flex-col tw:gap-8 tw:pt-6">
        <div className="tw:flex tw:flex-col tw:gap-2">
          <div className="tw:flex tw:items-center tw:justify-between tw:font-medium">
            <span>{i18n.t('EventOrder:costSummary')}</span>
          </div>
          <div className="tw:flex tw:items-center tw:justify-between tw:text-sm tw:font-normal">
            <span>{i18n.t('EventOrder:subtotal')}</span>
            <span>{centsToPrice(subtotalCents, currency)}</span>
          </div>

          <div className="tw:flex tw:items-center tw:justify-between tw:text-sm">
            <span className="tw:flex tw:items-center tw:gap-1.5">
              {i18n.t('EventOrder:cleaningFee')}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="tw:size-6 tw:cursor-help"
                  >
                    <SvgIcon name="question-circle" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="tw:max-w-xs">
                    {minimumCleaningFeeRate != null
                      ? i18n.t('EventOrder:cleaningFeeTooltip', {
                          rate: minimumCleaningFeeRate,
                        })
                      : i18n.t('EventOrder:cleaningFeeTooltipNoMinimum')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </span>
            <span>{centsToPrice(cleaningFeeCents, currency)}</span>
          </div>

          <div className="tw:flex tw:items-center tw:justify-between tw:text-sm">
            <span>
              {i18n.t('EventOrder:taxes')} ({taxPercentage}%)
            </span>
            <span>{centsToPrice(taxCents, currency)}</span>
          </div>

          <div className="tw:flex tw:items-center tw:justify-between tw:text-sm tw:font-medium">
            <span>{i18n.t('EventOrder:total')}</span>
            <span>{centsToPrice(totalCents, currency)}</span>
          </div>
        </div>
        {children}
        <p className="tw:text-muted-foreground tw:text-xs">
          <BindingOrderTermsText />
        </p>
      </CardContent>
    </Card>
  );
}

CostSummary.Connected = function CostSummaryConnected({
  className,
}: {
  className?: string;
}) {
  const { currency, order, taxPercentage, minimumCleaningFeeRate } =
    useOrderFormContext();
  return (
    <CostSummary.ConnectedBase
      currency={currency}
      order={order}
      taxPercentage={taxPercentage}
      minimumCleaningFeeRate={minimumCleaningFeeRate}
      className={className}
    >
      <OrderField.Comment />
    </CostSummary.ConnectedBase>
  );
};

CostSummary.ConnectedBase = function CostSummaryConnectedBase({
  currency,
  order,
  taxPercentage,
  minimumCleaningFeeRate,
  children,
  className,
}: {
  currency: string | null;
  order: ContainerOrder;
  taxPercentage: number;
  minimumCleaningFeeRate: number | null;
  children?: ReactNode;
  className?: string;
}) {
  const subtotalCents = useMemo(() => {
    let sum = 0;

    Object.values(order).forEach((iL) => {
      Object.values(iL.containers).forEach((c) => {
        let quantity = c.quantity;

        sum += c.price.rentalPrice * quantity;
      });
    });
    return sum;
  }, [order]);

  const cleaningFeeCents = useMemo(() => {
    // No minimum cleaning fee: only items returned dirty are billed after usage
    if (minimumCleaningFeeRate == null) {
      return 0;
    }

    // Mirror the backend invoice logic: the minimum cleaning quantity is
    // smartFloor(delivered pcs * rate%) per container type, billed at the
    // cleaning price per piece
    const perContainerType = new Map<
      number,
      { pieces: number; cleaningPrice: number }
    >();
    Object.values(order).forEach((iL) => {
      Object.values(iL.containers).forEach((c) => {
        const entry = perContainerType.get(c.info.id) ?? {
          pieces: 0,
          cleaningPrice: c.price.cleaningPrice,
        };
        entry.pieces += c.quantity * c.info.packingUnit;
        perContainerType.set(c.info.id, entry);
      });
    });

    let sum = 0;
    perContainerType.forEach(({ pieces, cleaningPrice }) => {
      sum +=
        smartFloor((pieces * minimumCleaningFeeRate) / 100) * cleaningPrice;
    });
    // Round to nearest cent
    return Math.round(sum);
  }, [order, minimumCleaningFeeRate]);

  // Tax is calculated on subtotal + cleaning fee
  const baseForTax = subtotalCents + cleaningFeeCents;
  const taxCents = (baseForTax * taxPercentage) / 100;
  const totalCents = subtotalCents + cleaningFeeCents + taxCents;

  return (
    <CostSummary
      subtotalCents={subtotalCents}
      taxPercentage={taxPercentage}
      taxCents={taxCents}
      cleaningFeeCents={cleaningFeeCents}
      minimumCleaningFeeRate={minimumCleaningFeeRate}
      totalCents={totalCents}
      currency={currency}
      className={className}
    >
      {children}
    </CostSummary>
  );
};

function BindingOrderTermsText() {
  const text = i18n.t('EventOrder:bindingOrderTerms', {
    termsLink: '{{TERMS_LINK}}',
  });
  const [before, after] = text.split('{{TERMS_LINK}}');

  return (
    <>
      {before}
      <a
        className="tw:underline tw:underline-offset-4 tw:hover:text-primary"
        href="https://www.vytal.org/footer/agb"
        target="_blank"
        rel="noreferrer"
      >
        {i18n.t('EventOrder:termsAndConditionsLink')}
      </a>
      {after}
    </>
  );
}
